import { useTranslation } from 'react-i18next';
import { Badge, Group, NumberInput, Stack, Switch, Text } from '@mantine/core';
import { decodeEntities } from '@/lib/format';
import type { QaOverrides } from '@/lib/buyAcceptance';
import type { QualityAcceptanceProperty } from '@/types/buy';

interface BuyAcceptanceFieldsProps {
  /** Свойства приёмки культуры (qaDefaults: имя + ГОСТ-значение). */
  readonly qaProps: readonly QualityAcceptanceProperty[];
  readonly byGost: boolean;
  readonly onByGost: (value: boolean) => void;
  readonly overrides: QaOverrides;
  readonly onOverride: (name: string, field: 'inc' | 'dec', value: number | string) => void;
  /** qaDefaults ещё грузится — переключатель недоступен. */
  readonly loading?: boolean;
  /** Свойства загрузились и их нет — режим «не по ГОСТу» невозможен (бэкенд требует непустой список). */
  readonly noProps?: boolean;
}

// Приёмка по сорности/влажности/примесям: переключатель «Только по ГОСТу» + либо ГОСТ-бейджи, либо
// по каждому свойству правило «Если {параметр} превышает [N%] → снижение веса на [M%]». Контролируемый
// компонент — единый для формы прайса и сделки «Купить» из свободного поиска (зачёт качества НЕ здесь:
// в сделку он не входит, живёт только в прайсе).
export default function BuyAcceptanceFields({
  qaProps,
  byGost,
  onByGost,
  overrides,
  onOverride,
  loading,
  noProps,
}: BuyAcceptanceFieldsProps) {
  const { t } = useTranslation();
  return (
    <Stack gap="xs">
      <Text fw={600}>{t('app.buy.terms.acceptance')}</Text>
      <Switch
        checked={byGost}
        onChange={(event) => onByGost(event.currentTarget.checked)}
        label={t('app.buy.terms.acceptanceGost')}
        disabled={loading || noProps}
      />
      {noProps ? (
        <Text size="sm" c="gray.7">
          {t('app.buy.form.gostOnly')}
        </Text>
      ) : null}
      {byGost ? (
        qaProps.length > 0 ? (
          <Group gap={4}>
            {qaProps.map((property) => (
              <Badge key={property.name} variant="default" tt="none" fw={500} size="lg" radius="sm" c="gray.9">
                {property.name}
                {property.gostValue ? ` ${decodeEntities(property.gostValue)}` : ''}
              </Badge>
            ))}
          </Group>
        ) : null
      ) : qaProps.length > 0 ? (
        <Stack gap="xs">
          {qaProps.map((property) => {
            const override = overrides[property.name] ?? { inc: '', dec: '' };
            return (
              <Group key={property.name} gap={8} wrap="wrap" align="center">
                <Text size="sm">
                  {t('app.buy.form.qaRuleIf')}{' '}
                  <Text component="span" fw={600}>
                    {property.name}
                  </Text>{' '}
                  {t('app.buy.form.qaRuleExceeds')}
                </Text>
                <NumberInput
                  value={override.inc}
                  onChange={(value) => onOverride(property.name, 'inc', value)}
                  min={0}
                  allowNegative={false}
                  decimalScale={1}
                  suffix=" %"
                  hideControls
                  size="xs"
                  w={90}
                  error={override.inc === ''}
                  aria-label={`${property.name}: ${t('app.buy.form.qaRuleExceeds')}`}
                />
                <Text size="sm">→ {t('app.buy.form.qaRuleThen')}</Text>
                <NumberInput
                  value={override.dec}
                  onChange={(value) => onOverride(property.name, 'dec', value)}
                  min={0}
                  allowNegative={false}
                  decimalScale={1}
                  suffix=" %"
                  hideControls
                  size="xs"
                  w={90}
                  error={override.dec === ''}
                  aria-label={`${property.name}: ${t('app.buy.form.qaRuleThen')}`}
                />
              </Group>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}
